/**
 * Route `/wizard`: il percorso di benvenuto collegato all'API (API-CONTRACT §3).
 * Gli stati sono quattro e si vedono tutti: caricamento, domanda, pausa, errore con rimedio.
 * Nessun dato finto: se il server non risponde si dice, non si inventa una card.
 */
import { useEffect, useState } from 'react'

import {
  ConfirmFactSheet,
  ErrorMessage,
  PrimaryButton,
  ProgressWords,
  QuestionTitle,
  SecondaryButton,
  WhyText,
  WizardFrame,
} from '../components'
import { applyAppName, askAgentLabel } from '../appName'
import { AskAgentPanel } from './AskAgentPanel'
import { QuestionScreen } from './QuestionScreen'
import { SummaryScreen } from './SummaryScreen'
import { useWizard } from './useWizard'

function lowerFirst(text: string): string {
  return text ? text[0].toLowerCase() + text.slice(1) : text
}

export function WizardRoute() {
  const { state, load, answer, escape, decideFact, decideSummaryFact, pause, resume, complete } = useWizard()
  const [chiedi, setChiedi] = useState(false)

  useEffect(() => {
    void applyAppName(state.displayForm)
  }, [state.displayForm])

  if (state.phase === 'caricamento') {
    return (
      <Semplice titolo="Un momento" testo="Sto riprendendo da dove eravamo.">
        <p className="text-body text-ink" role="status" data-testid="caricamento">
          Caricamento…
        </p>
      </Semplice>
    )
  }

  if (state.phase === 'errore' && state.error) {
    return (
      <Semplice titolo="Non sono riuscita a salvare" testo="Quello che ha scritto è ancora nel campo.">
        <ErrorMessage error={state.error} onRetry={() => void load()} />
      </Semplice>
    )
  }

  if (state.phase === 'pausa') {
    return (
      <Semplice
        titolo="Ci fermiamo qui"
        testo="Riprendiamo da questa domanda quando vuole: non si perde niente."
      >
        <PrimaryButton disabled={state.busy} onClick={() => void resume()}>
          Riprendiamo
        </PrimaryButton>
        <div className="mt-3">
          <SecondaryButton onClick={() => window.location.assign('/oggi')}>Vai alla sua giornata</SecondaryButton>
        </div>
      </Semplice>
    )
  }

  if (state.phase === 'chiuso') {
    return (
      <Semplice titolo="Abbiamo finito" testo="Quello che mi ha detto è al suo posto, e può cambiarlo quando vuole.">
        <PrimaryButton onClick={() => window.location.assign('/oggi')}>Vai alla sua giornata</PrimaryButton>
        <div className="mt-3">
          <SecondaryButton onClick={() => void load()}>Rivedi il riepilogo</SecondaryButton>
        </div>
      </Semplice>
    )
  }

  if (state.phase === 'riepilogo' && state.summary) {
    return (
      <SummaryScreen
        key="riepilogo"
        summary={state.summary}
        busy={state.busy}
        onDecide={(factId, valueText, decisione) => void decideSummaryFact(factId, valueText, decisione)}
        onComplete={() => void complete()}
      />
    )
  }

  if (!state.card) {
    return (
      <Semplice titolo="Un momento" testo="Sto riprendendo da dove eravamo.">
        <SecondaryButton onClick={() => void load()}>Riprova</SecondaryButton>
      </Semplice>
    )
  }

  return (
    <div className="h-full" data-question-id={state.card.id}>
      <QuestionScreen
        card={state.card}
        recap={state.recap}
        askAgentLabel={askAgentLabel(state.displayForm)}
        busy={state.busy || state.phase === 'conferma'}
        onAnswer={(scelte, testo) => answer(state.card!, scelte, testo)}
        onEscape={(kind) => escape(state.card!, kind)}
        onAskAgent={() => setChiedi(true)}
        onPause={() => void pause()}
      />
      {state.phase === 'conferma' && state.pendingFact && (
        <ConfirmFactSheet
          statement={lowerFirst(state.pendingFact.value_text)}
          onYes={() => void decideFact('conferma')}
          onNo={() => void decideFact('togli')}
          onCorrect={() => void decideFact('correggi')}
        />
      )}
      {chiedi && (
        <AskAgentPanel label={askAgentLabel(state.displayForm)} onClose={() => setChiedi(false)} />
      )}
    </div>
  )
}

function Semplice({ titolo, testo, children }: { titolo: string; testo: string; children: React.ReactNode }) {
  return (
    <WizardFrame band={<ProgressWords block="Percorso di benvenuto" progress="Un passo per volta" />}>
      <QuestionTitle>{titolo}</QuestionTitle>
      <div className="mb-auto">
        <WhyText>{testo}</WhyText>
      </div>
      <div className="mt-4">{children}</div>
    </WizardFrame>
  )
}
